import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, usdFromMicros, retryRead, readRetryDelay, campaignPollInterval } from '../api.js';
import { StatusPill } from './Dashboard.jsx';
import { useAccess } from '../access.js';
import { CampaignBudget } from '../components/CampaignBudget.jsx';

export default function CampaignDetail() {
  const { id } = useParams();
  const { can } = useAccess();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: ['campaign', id] });
  const planM = useMutation({ mutationFn: () => api.planCampaign(id), onSuccess: refresh });
  const startM = useMutation({ mutationFn: () => api.startCampaign(id), onSuccess: refresh });
  const campaignQ = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.getCampaign(id),
    refetchInterval: campaignPollInterval,
    retry: retryRead,
    retryDelay: readRetryDelay,
    refetchIntervalInBackground: false,
  });

  if (campaignQ.isLoading) return <div className="text-slate-500">Carregando…</div>;
  if (campaignQ.error && (!campaignQ.data || [401, 403].includes(campaignQ.error.status)))
    return (
      <div role="alert" className="space-y-3 text-red-600">
        <p>{campaignQ.error.message}</p>
        <button className="btn-secondary" onClick={() => campaignQ.refetch()}>
          Tentar novamente
        </button>
      </div>
    );

  const c = campaignQ.data;
  return (
    <div className="space-y-6">
      {campaignQ.error && (
        <div
          role="alert"
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm space-y-2"
        >
          <p>
            A atualização da tela foi interrompida. Os dados abaixo são da última consulta; a
            campanha pode continuar no servidor.
          </p>
          <p>{campaignQ.error.message}</p>
          <button
            className="btn-secondary"
            disabled={campaignQ.isFetching}
            onClick={() => campaignQ.refetch()}
          >
            Atualizar andamento
          </button>
        </div>
      )}
      <div className="flex items-baseline gap-4">
        <h1 className="text-2xl font-semibold">{c.name}</h1>
        <StatusPill status={c.status} />
      </div>
      <p className="text-slate-600">{c.intent_text}</p>
      {c.status === 'running' && (
        <div role="status" className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-2">
          <p className="font-medium">
            {c.progress?.discovered ? 'Avaliando as empresas encontradas' : 'Buscando empresas'}
          </p>
          <p className="text-sm">
            {c.progress?.discovered
              ? `${c.progress.discovered} resultados na busca · ${c.leadCount} leads registrados · ${c.progress.scored} empresas avaliadas pela IA.`
              : 'Aguardando os resultados da consulta.'}
          </p>
          <p className="text-sm text-slate-600">
            A avaliação é feita uma empresa por vez e pode levar alguns minutos. Você pode sair
            desta página e voltar depois. O andamento é atualizado a cada 10 segundos.
          </p>
        </div>
      )}
      {c.target_definition?.executionError && (
        <div role="alert" className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-2">
          <p className="font-medium">
            {c.status === 'paused_budget'
              ? 'Campanha pausada por limite de gasto'
              : 'Não foi possível concluir a campanha'}
          </p>
          <p className="text-sm">{c.target_definition.executionError.message}</p>
          {c.status === 'paused_budget' && can('tenant.settings.manage') && (
            <Link to="/settings" className="text-sm text-brand-700 underline">
              Ajustar limite mensal do tenant
            </Link>
          )}
        </div>
      )}
      {['draft', 'ready', 'paused_budget'].includes(c.status) && can('campaigns.write') && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <p className="text-sm text-slate-600">
            Atualize o plano após alterar os provedores do tenant. Confira a estimativa antes de
            iniciar.
          </p>
          {c.target_definition?.plan && (
            <p className="text-sm">
              {c.target_definition.plan.keywords.join(', ')} ·{' '}
              {c.target_definition.plan.cities.join(', ')}
            </p>
          )}
          {c.target_definition?.plan?.mapsConfigured === false && (
            <p className="text-sm text-amber-800">
              Google Maps ainda não configurado. Solicite a configuração ao administrador.
            </p>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              className="btn-secondary"
              disabled={planM.isPending || startM.isPending}
              onClick={() => planM.mutate()}
            >
              {planM.isPending ? 'Planejando…' : 'Atualizar plano'}
            </button>
            {can('campaigns.run') && ['ready', 'paused_budget'].includes(c.status) && (
              <button
                type="button"
                className="btn-primary"
                disabled={
                  planM.isPending ||
                  startM.isPending ||
                  c.target_definition?.plan?.mapsConfigured === false
                }
                onClick={() => startM.mutate()}
              >
                {startM.isPending
                  ? 'Iniciando…'
                  : c.status === 'paused_budget'
                    ? 'Retomar campanha'
                    : 'Iniciar campanha'}
              </button>
            )}
          </div>
          {(planM.error || startM.error) && (
            <p role="alert" className="text-red-600 text-sm">
              {(planM.error || startM.error).message}
            </p>
          )}
          {can('campaigns.run') && (
            <CampaignBudget key={`${c.id}:${c.max_cost_microusd}`} campaign={c} onSaved={refresh} />
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat
          label="Estimativa Maps"
          value={(Number(c.estimated_cost_microusd ?? 0) / 1_000_000).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 3,
          })}
        />
        <Stat label="Limite da campanha" value={usdFromMicros(c.max_cost_microusd)} />
        <Stat label="Gasto" value={usdFromMicros(c.actual_cost_microusd)} />
        <Stat label="Leads" value={c.leadCount} />
        <Stat label="Queries" value={c.queries.length} />
      </div>

      <Section title="Custos por provider/feature">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Provider</th>
              <th className="text-left px-3 py-2">Feature</th>
              <th className="text-right px-3 py-2">Calls</th>
              <th className="text-right px-3 py-2">Gasto</th>
            </tr>
          </thead>
          <tbody>
            {(c.costs ?? []).map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-2">{r.provider}</td>
                <td className="px-3 py-2">{r.feature_key}</td>
                <td className="px-3 py-2 text-right">{r.calls}</td>
                <td className="px-3 py-2 text-right">{usdFromMicros(r.spent_micros)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Link to={`/leads?campaignId=${id}`} className="text-sm text-brand-700 hover:underline">
        Filtrar e exportar leads desta campanha →
      </Link>
      <Section title="Leads">
        {c.leads?.length ? (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2">Empresa</th>
                <th className="text-left px-3 py-2">Domínio</th>
                <th className="text-left px-3 py-2">Telefone</th>
                <th className="text-right px-3 py-2">ICP</th>
              </tr>
            </thead>
            <tbody>
              {c.leads.map((row) => (
                <tr key={row.tenant_lead_id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">
                    <Link
                      to={`/leads/${row.tenant_lead_id}`}
                      state={{ from: `/campaigns/${id}` }}
                      className="text-brand-700 hover:underline"
                    >
                      {row.canonical_name}
                    </Link>
                    <Link
                      to={`/leads/${row.tenant_lead_id}`}
                      state={{ from: `/campaigns/${id}` }}
                      className="block mt-1 text-xs text-brand-700 hover:underline"
                    >
                      Ver detalhes →
                    </Link>
                  </td>
                  <td className="px-3 py-2">{row.primary_domain}</td>
                  <td className="px-3 py-2">{row.primary_phone}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {row.intelligence?.score ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-4 text-sm text-slate-500">Nenhum lead ainda.</div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 text-sm font-medium text-slate-700">
        {title}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-slate-500 text-xs uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
